import MeanMedianMode from "../meanMedianMode/meanMedianMode";
import Atom from "../chemistry/atom";
import TerrainCar from "../terrainCar/terrainCar";
import CoilEmField from "../coilEmField/coilEmField";
import Molecule from "../chemistry/molecule";
import Network from "../miniviews/network";
import { HotspotMesh, ICON_SHAPE } from "../assets/sprites/IconTexture";
import RenderManager from "../rendering/render-manager";
import AddInteractiveUI from "./interactive-ui";
import FerrisWheel from "./ferris-wheel";
import Racetrack from "./racetrack";
import NexusMesh from "./nexus-mesh";
import SlideManager from "../utils/slide-manager";
import Star from "./star/star";
import Water from "./water";
import MaterialBuilder from "../rendering/material-builder";
import { fire } from "../utils/events";
import Spline from "../assets/spline";
import AssetLibrary from "../assets/asset-library";
import { addTeachingCylinder } from "../geometry-teaching/interactable-geometry";
import loadObject, { finalizeObject, bindSkeleton } from "../assets/deserialize-object";
import cloneObject from "../../editor/helpers/cloneObject";
import ScalpelPlane from "./scalpel-plane";
import MolecularMesh from "../chemistry/molecular-mesh";
import ProceduralObject from "./procedural/procedural-object";
import ProceduralMesh from "./procedural/procedural-mesh";
import AssessmentManager from "../assessment/assessment-manager";

const MAX_FACES = 100000;
const OVERFLOW_MESSAGE = "BEWARE: if you display a high polycount 3D asset it will affect performance and loading times.";
const HAS_MORPH_TARGETS = "BEWARE: objects with morph targets have big file sizes.";
const tmpInverse = new THREE.Matrix4();

const ObjectManager = new class ObjectManager {
    _transformCache = new Map();

    constructor() { }

    countFaces(object) {
        let triangles = 0;
        object.traverse(object => {
            if (!object.isMesh || !object.geometry) return;

            const index = object.geometry.getIndex();
            const vertices = !index && object.geometry.getAttribute("position");

            triangles = triangles + (index ? index.count : vertices ? vertices.count : 0) / 3;
        });

        return triangles;
    }

    checkOverflow(object) {
        if (SlideManager.activeSlide >= 3) return false;
        return this.countFaces(object) >= MAX_FACES;
    }

    get morphTargetsMessage() { return HAS_MORPH_TARGETS; }
    get maxFaces() { return MAX_FACES; }
    warnOverflown() { console.warn(OVERFLOW_MESSAGE); }

    warnIfOverflown(object) {
        if (this.checkOverflow(object)) this.warnOverflown();
    }

    confirmIfOverflown(object) {
        if (this.checkOverflow(object)) return confirm(OVERFLOW_MESSAGE);
        return true;
    }

    createSkeleton(uuid) {
        bindSkeleton({
            uuid,
            data: Q3.data,
            object: AssetLibrary.objects[uuid],
            objects: AssetLibrary.objects
        });
    }

    async fromData(uuid, data) {
        const { geometries, materials } = AssetLibrary;

        const { object } = await loadObject({
            uuid, data,
            geometries, materials,
            visible: true
        });

        const activeSlide = SlideManager.activeSlide;
        const slides = SlideManager.slides;

        finalizeObject({
            object,
            slides,
            objects: [{ uuid, object }, { uuid: data.parent, object: AssetLibrary.objects[data.parent] }],
            uuid,
            data: Q3.data.assets.objects,
            materials,
            activeSlide
        });

        return object;
    }

    addObject(objectType, ...parameters) {
        switch (objectType) {
            case "empty": return addEmpty(...parameters);
            case "sphere": return addSphere(...parameters);
            case "cube": return addCube(...parameters);
            case "ring": return addRing(...parameters);
            case "plane": return addPlane(...parameters);
            case "atom": return addAtom(...parameters);
            case "terrain-car": return addTerrainCar(...parameters);
            case "math-cylinder": return addCylinder(...parameters);
            case "coil-em-field": return addCoilEmField(...parameters);
            case "mean-median-mode": return addMeanMedianMode(...parameters);
            case "light": return addLight(...parameters);
            case "arrow": return addArrow(...parameters);
            case "molecule": return addMolecule(...parameters);
            case "molecular-mesh": return addMolecularMesh(...parameters);
            case "gate": return addGate(...parameters);
            case "spline": return addSpline(...parameters);
            case "hotspot": return addHotspot(...parameters);
            case "ferris": return addFerrisWheel(...parameters);
            case "racetrack": return addRacetrack(...parameters);
            case "nexus": return addNexus(...parameters);
            case "star": return addStar(...parameters);
            case "water": return addWater(...parameters);
            case "scalpel-plane": return addScalpelPlane(...parameters);
            case "procedural-object": return addProceduralObject(...parameters);
            case "procedural-mesh": return addProceduralMesh(...parameters);
            default: throw new Error(`Unknown object type: ${objectType}`);
        }
    }

    getRestoreTransform(uuid, type) {
        if (!Q3.data.assets.objects[uuid]) return null;

        switch (type) {
            case "scale":
                return this._transformCache.has(uuid)
                    ? this._transformCache.get(uuid)[type]
                    : new THREE.Vector3(1, 1, 1);
            case "rotation":
                return this._transformCache.has(uuid)
                    ? this._transformCache.get(uuid)[type]
                    : new THREE.Euler();
            case "position":
                return new THREE.Vector3()
                    .copy(Q3.controls.target)
                    .applyMatrix4(
                        tmpInverse
                            .getInverse(
                                (Q3.assets.objects[uuid] || Q3.assets.lights[uuid])
                                    .parent
                                    .matrixWorld
                            )
                    );
            default: throw new Error(`Unknown restore of type: ${type}`);
        }
    }

    cacheRestoreTransform(uuid) {
        const object = Q3.data.assets.objects[uuid];

        if (!object) return;

        const { rotation, scale } = object;

        this._transformCache.set(uuid, {
            scale: new THREE.Vector3(scale.x, scale.y, scale.z),
            rotation: new THREE.Euler(rotation.x, rotation.y, rotation.z, rotation.order)
        });
    }

    deleteObject(object) {
        const state = this.getDeletionState(object);
        state[SlideManager.activeSlide] = true;

        if (state.every(x => x === true)) this.deleteObjectFromTour(object)
        else this.deleteObjectFromSlide(object, SlideManager.currentSlide, true);
    }

    deleteObjectFromTour(object) {
        const deletedAssessments = [];

        object.traverse(object => {
            Q3.SlideManager.slides.forEach(slide => {
                if (!slide.slideQuiz?.meshes) return;

                const assIndex = slide.slideQuiz.meshes.findIndex(meshes => {
                    return object === meshes.object
                });

                if (assIndex === -1) return;

                const ass = slide.slideQuiz.meshes[assIndex];

                deletedAssessments.push(ass);
                slide.slideQuiz.meshes.splice(assIndex, 1);
            });
        });

        deletedAssessments.forEach(ass => Q3.AssessmentManager.destroy(ass));

        Q3.data.slides.forEach(slide => this.deleteObjectFromSlide(object, slide));

        const parentUuid = object.parent.uuid;
        const parentData = Q3.data.assets.objects[parentUuid];

        if (parentData && parentData.isHiddenRoot) {
            const parent = Q3.assets.objects[parentUuid]

            parent.remove(object);

            if (parent.children.length === 0) {
                this.deleteObjectFromTour(parent);
            }
        }

        const removedAnims = {};

        if (Q3.data.assets.animations !== undefined && Q3.data.assets.animations !== null) {
            Object
                .keys(Q3.data.assets.animations)
                .forEach(animationUuid => {
                    const animation = Q3.data.assets.animations[animationUuid];

                    if (animation.root === object.uuid || animation.object === object.uuid) {
                        animation.deleted = true;
                        removedAnims[animationUuid] = true;
                    }
                });
        }

        for (let [slideIndex, slideData] of Q3.data.slides.entries()) {
            let { controls } = slideData;

            if (controls.interactionType === 'asset' && removedAnims[controls.asset]) {
                controls.interactionType = 'objectRotation';

                if (slideIndex === SlideManager.activeSlide) {
                    Q3.RenderManager.clearCameraAsset();

                    Q3.controls = Q3.objectRotationControls;
                    Q3.objectRotationControls.enabled = true;
                }
            }
        }

        fire('delete_object', { uuid: object.uuid });
    }

    deleteObjectFromSlide(object, slide, toggle) {
        object = Q3.data.assets.objects[object.overridePicker?.uuid]?.type === "InteractableGeometry"
            ? object.overridePicker
            : object;

        const anim = slide.animation[object.uuid];

        anim.deleted = toggle === true ? !anim.deleted : true;
        object.visible = anim.deleted
            ? false
            : anim.visible === undefined
                ? true
                : anim.visible;

        const ass = AssessmentManager.getAssessment(object);

        if (ass) AssessmentManager.destroy(ass);

        object?.children.forEach(ch => this.deleteObjectFromSlide(ch, slide, toggle));
    }

    getDeletionState(object) {
        const uuid = Q3.data.assets.objects[object.overridePicker?.uuid]?.type === "InteractableGeometry"
            ? object.overridePicker.uuid
            : object.uuid;

        return Q3.data.slides.map(slide => {
            return slide.animation[uuid].deleted === true ||
                slide.animation[uuid].visible === false;
        });
    }

    async duplicateObject(objectUuid) {
        const mesh = Q3.assets.objects[objectUuid] || Q3.assets.lights[objectUuid];

        let root = mesh;
        while (root.parent && root.parent !== RenderManager.activeScene) root = root.parent;

        const objectRewrite = {}, skeletons = [];

        function getNewUuid(oldUuid) {
            return objectRewrite[oldUuid] = objectRewrite[oldUuid] || THREE.Math.generateUUID();
        }

        const clone = async (parent, oldNode, isRoot) => {
            const currentSlide = SlideManager.currentSlide;
            const oldUuid = oldNode.uuid, newUuid = getNewUuid(oldUuid);
            const oldData = Q3.data.assets.objects[oldNode.uuid];
            const newData = Object.assign(cloneObject(oldData), {
                name: `${oldData.name}${isRoot ? " (Clone)" : ""}`
            });
            const oldAnimData = currentSlide.animation[oldUuid];

            Q3.data.assets.objects[newUuid] = newData;

            if (Q3.data.assets.skeletons && Q3.data.assets.skeletons[oldUuid]) {
                const skeleton = Q3.data.assets.skeletons[newUuid] = cloneObject(Q3.data.assets.skeletons[oldUuid]);
                skeleton.bones = skeleton.bones.map(boneUuid => getNewUuid(boneUuid));
                skeletons.push(newUuid);
            }

            if (oldData.material) {
                async function createMaterial(oldMatUuid) {
                    const newMatUuid = THREE.Math.generateUUID();
                    const oldMatData = Q3.data.assets.materials[oldMatUuid];
                    const newMatData = cloneObject(oldMatData);

                    Q3.data.assets.materials[newMatUuid] = newMatData;
                    AssetLibrary.materials[newMatUuid] = await MaterialBuilder.fromData(newMatUuid, newMatData);

                    if (currentSlide.materialAnimation) {
                        const oldMatAnimData = currentSlide.materialAnimation[oldMatUuid] || {};
                        for (let i = 0, len = SlideManager.slideCount; i < len; i++) {
                            const slide = SlideManager.getSlide(i);

                            if (!slide.materialAnimation) continue;

                            const newMatAnimData = cloneObject(oldMatAnimData);

                            slide.materialAnimation[newMatUuid] = newMatAnimData;
                        }
                    }

                    delete newMatData.isReference;
                    delete newMatData.referenceId;

                    return newMatUuid;
                }

                if (oldData.material instanceof Array)
                    newData.material = await Promise.all(oldData.material.map(async uuid => await createMaterial(uuid)));
                else newData.material = await createMaterial(oldData.material);
            }

            if (oldData.geometry) {
                const oldGeoUuid = oldData.geometry;
                const oldGeoData = Q3.data.assets.geometries[oldGeoUuid];

                delete oldGeoData.isReference;
                delete oldGeoData.referenceId;
            }

            delete newData.isReference;
            delete newData.referenceId;
            delete newData.refId;

            if (parent) newData.parent = parent;

            const newNode = AssetLibrary.objects[newUuid] = await this.fromData(newUuid, newData);

            const _isRoot = oldData.isHiddenRoot && isRoot;
            await Promise.all(oldNode.children.map(async node => {
                return await clone(newUuid, node, _isRoot);
            }));

            const animationRewrite = {};

            Object.keys(Q3.data.assets.animations || {}).forEach(oldAnimationUuid => {
                const oldAnimationData = Q3.data.assets.animations[oldAnimationUuid];

                if (oldAnimationData.object !== oldUuid) return;

                const newAnimationUuid = THREE.Math.generateUUID();

                Q3.data.assets.animations[newAnimationUuid] = {
                    name: oldAnimationData.name,
                    sourceAnimation: oldAnimationUuid,
                    path: oldAnimationData.path,
                    object: newUuid
                };

                AssetLibrary.animations[newAnimationUuid] = AssetLibrary.animations[oldAnimationUuid];
                animationRewrite[oldAnimationUuid] = newAnimationUuid;
            });


            for (let i = 0, len = SlideManager.slideCount; i < len; i++) {
                const slide = SlideManager.getSlide(i);
                slide.animation[newUuid] = cloneObject(oldAnimData);

                if (isRoot && !Q3.TransformModifiers.isTransformModifier(oldData))
                    slide.animation[newUuid].visible = false;

                const animationData = slide.animation[newUuid].animation;

                if (animationData && animationData.uuid) {
                    animationData.uuid = animationRewrite[animationData.uuid];
                }
            }

            currentSlide.animation[newUuid].visible = currentSlide.animation[oldUuid].visible;

            newNode.position.copy(oldNode.position);
            newNode.quaternion.copy(oldNode.quaternion);
            newNode.scale.copy(oldNode.scale);

            if (currentSlide.animation[newUuid].animation && currentSlide.animation[newUuid].animation.uuid) {
                const { uuid, type, timescale } = currentSlide.animation[newUuid].animation;
                Q3.mixer.playObjectAnimation({
                    objectUuid: newUuid,
                    animationUuid: uuid,
                    loopType: type,
                    timescale: timescale || 1
                });
            }

            return newUuid;
        }

        const clonedUuid = await clone(null, root, true);
        const clonedObject = AssetLibrary.objects[clonedUuid];

        skeletons.forEach(uuid => this.createSkeleton(uuid));

        Q3.selected = clonedObject.children.length === 0 ? clonedObject : clonedObject.children[0];
        fire("selectchange");
    }
}

export default ObjectManager;
export { ObjectManager };

function addSlideData(object, slideSettings = {}) {
    Q3.data.slides.forEach((slide, i) => {
        slide.animation[object.uuid] = Object.assign({
            position: { x: object.position.x, y: object.position.y, z: object.position.z },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 },
            visible: i === SlideManager.activeSlide
        }, slideSettings);
    });
}

function createBaseObjectData(object) {
    return {
        name: object.name,
        type: object.type,
        position: { x: object.position.x, y: object.position.y, z: object.position.z },
        rotation: { x: object.rotation.x, y: object.rotation.y, z: object.rotation.z },
        scale: { x: object.scale.x, y: object.scale.y, z: object.scale.z },
        parent_world: Network.currentWorldUuid
    };
}

function addBaseObjectData(object, origin = Q3.controls.target, quaternion) {
    object.position.copy(origin);

    if (quaternion) object.quaternion.copy(quaternion);

    AssetLibrary.objects[object.uuid] = object;

    Q3.data.assets.objects[object.uuid] = createBaseObjectData(object);
    return Q3.data.assets.objects[object.uuid];
}

function addMeshObjectData(object) {
    let material;
    if (object.material instanceof Array) {
        material = object.material.map(m => m.uuid);
    } else {
        material = object.material.uuid;
    }

    const data = Object.assign(addBaseObjectData(object), {
        geometry: object.geometry.uuid,
        material: material,
    });

    object.castShadow = object.receiveShadow = true;
    RenderManager.raycaster.makeTrackable(object);

    return data;
}

function addEmpty() {

    const object = new THREE.Object3D();
    object.name = "Empty object";

    addBaseObjectData(object);
    addSlideData(object);
    RenderManager.activeScene.add(object);

    return { object };

}

function createMaterial(name = "New material") {
    const material = MaterialBuilder.build();

    material.name = name;
    material.envMap = AssetLibrary.envLight.activeMap;
    material.envMapCubeRes = AssetLibrary.envLight.resolution;
    material.side = THREE.DoubleSide;

    AssetLibrary.materials[material.uuid] = material;
    Q3.data.assets.materials[material.uuid] = {
        type: 3,
        color: "0xffffff",
        name,
        slide: THREE.DoubleSide
    };

    return material;
}

function finalizeMeshObject(object) {
    RenderManager.activeScene.add(object);

    Q3.selected = object;

    RenderManager.activeCamera.update = true;
}

function addSphere() {

    const geometry = new THREE.SphereBufferGeometry(1, 30, 30);
    const material = createMaterial("New sphere material");

    const object = new THREE.Mesh(geometry, material);
    object.name = "Sphere";

    Q3.data.assets.geometries[geometry.uuid] = {
        type: "Sphere",
        radius: 1,
        widthSegments: 30,
        heightSegments: 30,
        name: "Sphere"
    };

    AssetLibrary.geometries[geometry.uuid] = geometry;

    addMeshObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };
}

function addCube() {

    const geometry = new THREE.BoxBufferGeometry(1, 1, 1);
    const material = createMaterial("New cube material");

    const object = new THREE.Mesh(geometry, material);
    object.name = "Cube";

    AssetLibrary.geometries[geometry.uuid] = geometry;
    Q3.data.assets.geometries[geometry.uuid] = {
        type: "Cube",
        width: 1,
        height: 1,
        depth: 1,
        name: "Cube"
    };

    addMeshObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };
}

function addNexusObjectData(object) {
    const data = Object.assign(addBaseObjectData(object), {
        geometry: object.nexusObject.geometry,
        material: object.nexusObject.material.uuid
    });

    object.castShadow = object.receiveShadow = true;

    return data;
}

function addNexus() {
    const availableGeometries = [
        // "/persistent_assets/test_models/1M/Body.nxz",
        "/persistent_assets/test_models/100k/hutsuls.nxs",
        // "/persistent_assets/test_models/10k/TwoGears_coobo_TestScene.nxz"
    ];

    const geometry = availableGeometries[Math.floor(Math.random() * availableGeometries.length)];
    const material = createMaterial("New cube material");

    const object = new NexusMesh(geometry, material);
    object.name = "Nexus";

    addNexusObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };
}

function addRing() {

    const geometry = new THREE.CustomRingBufferGeometry(0, 1, 30);
    const material = createMaterial("New ring material");

    const object = new THREE.Mesh(geometry, material);
    object.name = "Disc";

    AssetLibrary.geometries[geometry.uuid] = geometry;
    Q3.data.assets.geometries[geometry.uuid] = {
        type: "Ring",
        innerRadius: 0,
        outerRadius: 1,
        thetaSegments: 30,
        name: "Disc"
    };

    addMeshObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };

}

function addScalpelPlane() {
    const object = new ScalpelPlane();

    Object.assign(addBaseObjectData(object), {
        objects: [],
        ignoreDepth: true
    });

    RenderManager.activeScene.add(object);
    addSlideData(object);

    return { object };
}

function addPlane() {
    const geometry = new THREE.PlaneBufferGeometry(4, 4, 2, 2);
    const material = createMaterial("New plane material");

    const object = new THREE.Mesh(geometry, material);
    object.name = "Plane";

    AssetLibrary.geometries[geometry.uuid] = geometry;
    Q3.data.assets.geometries[geometry.uuid] = {
        type: "Plane",
        width: 4,
        height: 4,
        widthSegments: 2,
        heightSegments: 2,
        name: "Plane"
    };

    addMeshObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };
}

function addArrow() {

    const geometry = new THREE.ArrowBufferGeometry(1, 0.3, 0.2, 0.3, 0.4);
    const material = createMaterial("New arrow material");

    const object = new THREE.Mesh(geometry, material);
    object.name = "Arrow";

    AssetLibrary.geometries[geometry.uuid] = geometry;
    Q3.data.assets.geometries[geometry.uuid] = {
        type: "Arrow",
        width: 1,
        height: 0.3,
        depth: 0.2,
        arrowWidth: 0.3,
        arrowHeight: 0.4,
        name: "Arrow"
    };

    addMeshObjectData(object);
    addSlideData(object);
    finalizeMeshObject(object);

    return { object };
}

function addRacetrack() {
    const object = new Racetrack();
    object.name = "Racetrack";

    Object.assign(addBaseObjectData(object), {
        asset: object.trackUuid,
        trackLength: object.trackLength,
        trackRadius: object.trackRadius,
        actionUuid: object.actionUuid,
        axisOffset: object.axisOffset
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    return { object };
}

function addFerrisWheel() {
    if (Q3.ferris) return { object: null };

    const object = new FerrisWheel();
    object.name = "Ferris";

    Object.assign(addBaseObjectData(object), {});

    addSlideData(object);
    RenderManager.activeScene.add(object);

    return { object };
}

function addAtom() {
    const object = new Atom(1, 0, 1);
    object.name = "Atom";

    Object.assign(addBaseObjectData(object), {
        counts: { proton: 1, neutron: 0, electron: 1 },
        periodicTable: { visible: false, interactive: false },
        infoVisible: { box: true, labels: true },
        controlsVisible: true,
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    for (let [slide,] of Q3.data.slides.entries()) {
        AddInteractiveUI.atom(object, slide);
    }

    return { object };
}

function addTerrainCar() {
    const object = new TerrainCar();
    object.name = "Car";

    Object.assign(addBaseObjectData(object), {
        interactiveCar: JSON.parse(JSON.stringify(object.params)),
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    for (let [slide,] of Q3.data.slides.entries()) {
        AddInteractiveUI.terrainCar(object, slide);
    }

    return { object };
}

function addCylinder() {
    const object = addTeachingCylinder();
    const geo = object.children.find(x => x.isInteractableGeometry);

    object.name = "Interactive Cylinder";

    Object.assign(addBaseObjectData(object), {
        geometryType: geo.geometryType,
        type: geo.type
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    for (let [slide,] of Q3.data.slides.entries()) {
        AddInteractiveUI.cylinderInfo(object, slide);
    }

    return { object };
}

function addCoilEmField() {
    let paramCallback = (field, name, value) => {
        Q3.data.assets.objects[field.uuid].coilEmField[name] = value;
    };

    const object = new CoilEmField({ paramCallback });

    object.name = "Electromagnetic Field";

    Object.assign(addBaseObjectData(object), {
        coilEmField: {
            coilRadius: object.coilRadius,
            coilLength: object.coilLength,
            turns: object.turns,
            current: object.current,
            voltage: object.voltage,
            controlsEnabled: object.controlsEnabled,
        },
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    for (let [slide,] of Q3.data.slides.entries()) {
        AddInteractiveUI.emField(object, slide);
    }

    return { object };
}

function addProceduralObject() {
    const object = new ProceduralObject();

    const slideSettings = {
        offset: 0.0075
    };

    Object.assign(addBaseObjectData(object), {
        displayType: object.displayType,
        meshes: [],
        color: "#ffffff",
        edgeColor: "#000000",
        showEdges: true,
        edgeWidth: 1,
        opacity: 1
    }, slideSettings);

    addSlideData(object, slideSettings);

    object.setDisplayType(ProceduralObject.Shape_T.TETRAHEDRON);

    RenderManager.activeScene.add(object);

    return { object };
}

function addProceduralMesh(uuid, parentUuid, data, { geometry, text = "", material, name, position = new THREE.Vector3() }) {
    const object = new ProceduralMesh(geometry, material);

    object.uuid = uuid;
    object.name = name;

    Object.assign(addBaseObjectData(object, position), {
        parent: parentUuid,
        parent_world: data.parent_world,
        text
    });

    addSlideData(object);

    RenderManager.raycaster.makeTrackable(object);

    return { object };
}

function addMolecularMesh(uuid, parentUuid, data, { geometry, material, name, position = new THREE.Vector3(), quaternion }) {
    const object = new MolecularMesh(geometry, material);

    object.uuid = uuid;
    object.name = name;

    Object.assign(addBaseObjectData(object, position, quaternion), {
        parent: parentUuid,
        parent_world: data.parent_world
    });
    addSlideData(object);

    RenderManager.raycaster.makeTrackable(object);

    return { object };
}

async function addMolecule() {
    const object = new Molecule();
    object.name = "Molecule";

    const objectData = addBaseObjectData(object);
    const assetUri = await object.setFormula("ethanol", true);

    Object.assign(objectData, {
        formula: object.formula,
        displayType: object.displayType,
        meshes: "meshes" in objectData ? objectData.meshes : [],
        assetUri
    });

    addSlideData(object);

    RenderManager.activeScene.add(object);

    return { object };
}

function addGate() {

    const object = Network.createGate();
    object.name = "Scale portal";

    Object.assign(addBaseObjectData(object), {
        world_id: object.scene.uuid
    });

    addSlideData(object);

    RenderManager.activeScene.add(object);

    return { object };

}

function addMeanMedianMode() {
    let dragCallback = (mmm, index, value) => {
        Q3.data.assets.objects[mmm.uuid].meanMedianMode.values[index] = value;
    }

    const object = new MeanMedianMode({ dragCallback });

    object.name = "Mean/Median/Mode";
    object.setSampleSize(25);

    Object.assign(addBaseObjectData(object), {
        meanMedianMode: {
            size: [5, 5],
            values: object.getValues().slice(),
            modelIds: object.getModelIds().slice(),
            spacing: object.getSpacing(),
            showInfo: object.showInfo,
            showAllHeights: object.showAllHeights,
            showCalc: object.showCalc,
            showParams: {
                mean: true,
                median: true,
                mode: true,
            },
        },
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    for (let [slide, slideData] of Q3.data.slides.entries()) {
        AddInteractiveUI.mmmInfo(object, slide);
    }

    return { object };
}

function addSpline() {
    const object = new Spline();
    object.name = "New Spline";

    Object.assign(addBaseObjectData(object), {
        subType: object.subType,
        points: [{ x: -0.5, y: 0, z: 0 }, { x: 0.5, y: 0, z: 0 }],
        showCurve: true,
        tension: 0.5,
        thickness: 2,
        spriteDirection: 0.0,
        color: "#000000",
        closed: false,
    });

    addSlideData(object);
    RenderManager.activeScene.add(object);

    object.updateSpline();
    RenderManager.raycaster.makeTrackable(object);

    return { object };
}

function addLight() {

    const object = new THREE.PointLight();
    object.name = "Point light";

    Q3.data.assets.lights[object.uuid] = Object.assign(createBaseObjectData(object), {
        color: "0xffffff",
        intensity: 1,
        decay: 1,
        distance: 1000,
        castShadow: false,
    });

    AssetLibrary.lights[object.uuid] = object;

    addSlideData(object);
    RenderManager.activeScene.add(object);

    return { object };
}

function addHotspot() {
    const iconData = {
        size: 0, //Auto-sized
        shape: ICON_SHAPE.CIRCLE,
        backgroundColor: "#fff",
        backgroundOpacity: 0,
        borderColor: "#d8d821",
        borderWidth: 16,
        borderOpacity: 1,
        iconHas: false,
        iconUrl: "",
        iconColor: "#d8d821",
        iconSize: 50
    };

    const object = new HotspotMesh(iconData);
    object.name = "Hotspot";

    // Update data
    Q3.data.assets.materials[object.material.uuid] = {
        type: 7,
        flatShading: true,
        name: 'New Hotspot material',
        iconData
    };

    Object.assign(addBaseObjectData(object), {
        geometry: object.geometry.uuid,
        material: object.material.uuid,

        subType: object.subType,
    });

    Q3.data.assets.geometries[object.geometry.uuid] = {
        type: object.subType,
        mesh: object.uuid
    };

    // Update assets
    AssetLibrary.materials[object.material.uuid] = object.material;
    AssetLibrary.geometries[object.geometry.uuid] = object.geometry;

    addSlideData(object);

    RenderManager.raycaster.makeTrackable(object);
    RenderManager.activeScene.add(object);

    return { object };
}

function addWater() {
    const object = new Water();

    Object.assign(addBaseObjectData(object), {
        alpha: object.material.uniforms.alpha.value,
        distortionScale: object.material.uniforms.distortionScale.value,
        size: object.material.uniforms.size.value
    });

    addSlideData(object);

    RenderManager.activeScene.add(object);
    RenderManager.raycaster.makeTrackable(object);

    return { object };
}

function addStar() {
    const object = new Star();

    Object.assign(addBaseObjectData(object), {
        temperature: object.temperature,
    });

    addSlideData(object);

    RenderManager.activeScene.add(object);
    RenderManager.raycaster.makeTrackable(object.core);

    return { object };
}
